import { Box, Button, Card, CardContent, CardHeader, Divider, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { NumericFormat } from 'react-number-format';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from "moment";
import { DealBuilder } from "../services/deal-builder.ts";
import { Category, resolveCorrectionValues } from "../services/utils/sources-and-uses.utils.ts";
import useDealBuilderResultStore from "../../stores/deal-builder-result.store.ts";
import NewTicDisplay from "./shared/new-tic-display.tsx";
import formatter from "../../tools/formatter.js";
import BlurDatePicker from "./bonds-page/blur-date-picker.tsx";
import _ from "lodash";

interface SourcesAndUsesPageInput {
  builder: DealBuilder
}

// Fixed widths so the value columns line up across every row.
const DOLLAR_WIDTH = 140;
const PERCENT_WIDTH = 110;
const SELECT_WIDTH = 130;

type CorrectionTarget = "amount" | "par amount" | "debt service";

const inputSx = {
  '& .MuiInputBase-input': {
    fontSize: '14px',
    textAlign: 'right',
    fontWeight: 500,
  },
};

export default function SourcesAndUsesPage({ builder }: SourcesAndUsesPageInput) {
  const { setDealBuilderService, resetCopy } = useDealBuilderResultStore();

  const parAmount = builder.getTotalPrincipal();
  const debtService = builder.getTotalDebtService();

  // Custom sources function
  function onNewCategoryButton(type: Category["type"]) {
    const newCategory: Category = {
      title: "Unnamed",
      corrections: [],
      deletable: true,
      ticAdjusted: false,
      type: type
    };

    const categories = [...builder.sourcesAndUses, newCategory];
    builder.sourcesAndUses = categories;
    setDealBuilderService(builder);
  }

  function onRemoveCategoryButton(categoryIndex: number) {
    const categories = builder.sourcesAndUses.filter((_, i) => i !== categoryIndex);
    builder.sourcesAndUses = categories;
    setDealBuilderService(builder);
  }

  function onAddOptionButton(categoryIndex: number) {
    builder.sourcesAndUses[categoryIndex].corrections.push({
      title: "Unnamed",
      type: "amount",
      amount: 0,
    });
    setDealBuilderService(builder);
  }

  function onRemoveOptionButton(categoryIndex: number, optionIndex: number) {
    builder.sourcesAndUses[categoryIndex].corrections =
      builder.sourcesAndUses[categoryIndex].corrections.filter((_, i) => i !== optionIndex);
    builder.calculateStats();
    setDealBuilderService(builder);
  }

  function onEditOptionName(category: number, option: number, name: string) {
    builder.sourcesAndUses[category].corrections[option].title = name;
    setDealBuilderService(builder);
  }

  function onEditOptionValue(category: number, option: number, value: number) {
    builder.sourcesAndUses[category].corrections[option].amount = value;
    builder.calculateStats();
    setDealBuilderService(builder);
  }

  function onEditOptionType(category: number, option: number, target: CorrectionTarget) {
    const correction = builder.sourcesAndUses[category].corrections[option];

    // Resolve the current value to an actual dollar amount so the conversion
    // preserves the real value instead of reinterpreting the raw number.
    const { dollar } = resolveCorrectionValues(correction, parAmount, debtService);

    if (target === "amount") {
      builder.sourcesAndUses[category].corrections[option] = {
        title: correction.title,
        type: "amount",
        // Dollar amounts have no decimals.
        amount: Math.round(dollar),
      };
    } else {
      const base = target === "par amount" ? parAmount : debtService;
      // Percentages are rounded to the third decimal (e.g. 10.000%), and
      // stored as a fraction.
      const percent = base ? (dollar / base) * 100 : 0;
      builder.sourcesAndUses[category].corrections[option] = {
        title: correction.title,
        type: "percent",
        target,
        amount: Math.round(percent * 1000) / 1000 / 100,
      };
    }

    builder.calculateStats();
    setDealBuilderService(builder);
  }

  function onEditCategoryName(category: number, name: string) {
    builder.sourcesAndUses[category].title = name;
    setDealBuilderService(builder);
  }

  function reset() {
    setDealBuilderService(_.cloneDeep(resetCopy));
  }

  function renderCategories(type: Category["type"]) {
    return builder.sourcesAndUses.map((category, categoryIndex) => {
      if (category.type !== type) return null;

      return (
        <Box key={categoryIndex}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <TextField
              value={category.title}
              onChange={(e) => onEditCategoryName(categoryIndex, e.target.value)}
              variant="outlined"
              size="small"
              label="Category"
              sx={{
                flex: 1,
                maxWidth: 320,
                '& .MuiInputBase-input': { fontSize: '14px', fontWeight: 600 },
              }}
            />
            {category.deletable && (
              <DeleteOutlineIcon
                onClick={() => onRemoveCategoryButton(categoryIndex)}
                fontSize="small"
                color="error"
                sx={{ cursor: "pointer", ml: 1, "&:hover": { opacity: 0.75 } }}
              />
            )}
          </Stack>

          <Stack direction="column" gap={1} pl={2}>
            {category.corrections.length > 0 && <CorrectionHeader />}
            {category.corrections.map((option, optionIndex) => (
              <CorrectionRow
                key={optionIndex}
                option={option}
                parAmount={parAmount}
                debtService={debtService}
                onEditName={(name) => onEditOptionName(categoryIndex, optionIndex, name)}
                onEditValue={(value) => onEditOptionValue(categoryIndex, optionIndex, value)}
                onEditType={(target) => onEditOptionType(categoryIndex, optionIndex, target)}
                onRemove={() => onRemoveOptionButton(categoryIndex, optionIndex)}
              />
            ))}
            <Button
              onClick={() => onAddOptionButton(categoryIndex)}
              startIcon={<AddIcon />}
              variant="outlined"
              color="info"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Add option
            </Button>
          </Stack>
        </Box>
      );
    });
  }

  return (
    <Stack
      direction="column"
      gap={2}
      margin={2}
      sx={{ width: "100%" }}
    >
      <Paper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" m={1}>
          <Button
            variant="contained"
            color="error"
            sx={{ color: "white" }}
            disabled={resetCopy === undefined}
            onClick={reset}
          >Reset</Button>

          <NewTicDisplay builder={builder} />
        </Stack>
      </Paper>

      <Card elevation={3}>
        <CardHeader
          title="Sources"
          slotProps={{ title: { variant: 'subtitle1', fontWeight: 500 } }}
          sx={{ py: 1, px: 2 }}
        />
        <Divider />
        <CardContent sx={{ p: 2 }}>
          <Stack direction="column" gap={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Bond Proceeds</Typography>
              <Stack direction="column" pl={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Par Amount</Typography>
                  <Typography variant="subtitle2">
                    {formatter.money(builder.getTotalPrincipal())}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Premium</Typography>
                  <Typography variant="subtitle2">
                    {formatter.money(builder.getTotalProduction())}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {renderCategories("sources")}

            <Button
              onClick={() => onNewCategoryButton("sources")}
              startIcon={<AddIcon />}
              variant="contained"
              color="info"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Add category
            </Button>
          </Stack>
        </CardContent>
        <Divider />
        <CardContent sx={{ marginBottom: -1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600}>Total Sources</Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {formatter.money(builder.getTotalSourcesAmount())}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={3}>
        <CardHeader
          title="Uses"
          slotProps={{ title: { variant: 'subtitle1', fontWeight: 500 } }}
          sx={{ py: 1, px: 2 }}
        />
        <Divider />
        <CardContent sx={{ p: 2 }}>
          <Stack direction="column" gap={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Project Fund Deposits</Typography>
              <Stack direction="column" pl={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2">Project Fund</Typography>
                  <Typography variant="subtitle2">
                    {formatter.money(builder.calculateProjectFunds())}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {renderCategories("uses")}

            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Capitalized Interest</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" pl={2} gap={1}>
                <Stack direction="row" gap={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      size="small"
                      value={typeof builder.capitalizedInterest === "object" ? "time" : "amount"}
                      onChange={e => {
                        if (e.target.value === "time") {
                          builder.capitalizedInterest = builder.deliveryDate.clone().add(1, "year");
                        } else {
                          builder.capitalizedInterest = 0;
                        }
                        builder.updateDebtService();
                        setDealBuilderService(builder);
                      }}
                    >
                      <MenuItem value="time">Time based</MenuItem>
                      <MenuItem value="amount">Amount based</MenuItem>
                    </Select>
                  </FormControl>

                  {typeof builder.capitalizedInterest === "object" && (
                    <Box width="170px">
                      <BlurDatePicker
                        label="End Date"
                        format="MM/DD/YYYY"
                        value={moment(builder.capitalizedInterest)}
                        slotProps={{ textField: { size: 'small', sx: { width: '100%' } } }}
                        onChange={(value) => {
                          if (!value || !value.isValid()) return;
                          builder.capitalizedInterest = moment(value.toISOString());
                          builder.updateDebtService();
                          setDealBuilderService(builder);
                        }}
                        isDateValid={() => ({ valid: true })}
                      />
                    </Box>
                  )}
                </Stack>

                {typeof builder.capitalizedInterest === "number" ? (
                  <NumericFormat
                    value={builder.capitalizedInterest}
                    customInput={TextField}
                    thousandSeparator=","
                    prefix="$"
                    decimalScale={2}
                    variant="outlined"
                    size="small"
                    label="Amount"
                    allowNegative={false}
                    onValueChange={(values) => {
                      builder.capitalizedInterest = values.floatValue || 0;
                      builder.updateDebtService();
                      setDealBuilderService(builder);
                    }}
                    sx={{ width: DOLLAR_WIDTH, ...inputSx }}
                  />
                ) : (
                  <Typography variant="subtitle2">
                    {formatter.money(builder.getTotalCapitalizedInterest())}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Button
              onClick={() => onNewCategoryButton("uses")}
              startIcon={<AddIcon />}
              variant="contained"
              color="info"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Add category
            </Button>
          </Stack>
        </CardContent>
        <Divider />
        <CardContent sx={{ marginBottom: -1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600}>Total Uses</Typography>
            <Typography variant="subtitle2" fontWeight={600}>
              {formatter.money(builder.getTotalIssuanceCost())}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}

/**
 * Column captions aligned with the value inputs in each CorrectionRow.
 */
function CorrectionHeader() {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ width: DOLLAR_WIDTH, textAlign: "right" }}>
        Dollar
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ width: PERCENT_WIDTH, textAlign: "right" }}>
        Par %
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ width: PERCENT_WIDTH, textAlign: "right" }}>
        Debt Service %
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ width: SELECT_WIDTH }}>
        Editing
      </Typography>
      {/* spacer matching the delete icon */}
      <Box sx={{ width: 20 }} />
    </Stack>
  );
}

interface CorrectionRowInput {
  option: Category["corrections"][number];
  parAmount: number;
  debtService: number;
  onEditName: (name: string) => void;
  onEditValue: (value: number) => void;
  onEditType: (target: CorrectionTarget) => void;
  onRemove: () => void;
}

/**
 * A single source/use line item. Shows the dollar, par-amount and debt-service
 * representations side by side; only the representation selected in the
 * "Editing" dropdown is editable, the other two display derived values.
 */
function CorrectionRow({
  option,
  parAmount,
  debtService,
  onEditName,
  onEditValue,
  onEditType,
  onRemove,
}: CorrectionRowInput) {
  const { dollar, parPercent, debtServicePercent } = resolveCorrectionValues(option, parAmount, debtService);

  const selected: CorrectionTarget =
    option.type === "amount" ? "amount" : option.target;

  const isDollar = selected === "amount";
  const isPar = selected === "par amount";
  const isDebtService = selected === "debt service";

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <TextField
        value={option.title}
        onChange={(e) => onEditName(e.target.value)}
        variant="outlined"
        size="small"
        placeholder="Name"
        sx={{
          flex: 1,
          '& .MuiInputBase-input': { fontSize: '14px', fontWeight: 500 },
        }}
      />

      <NumericFormat
        value={dollar}
        customInput={TextField}
        thousandSeparator=","
        prefix="$"
        decimalScale={2}
        variant="outlined"
        size="small"
        disabled={!isDollar}
        onValueChange={(values) => { if (isDollar) onEditValue(values.floatValue || 0); }}
        sx={{ width: DOLLAR_WIDTH, ...inputSx }}
      />

      <NumericFormat
        value={parPercent}
        customInput={TextField}
        thousandSeparator=","
        suffix="%"
        decimalScale={3}
        allowNegative={false}
        variant="outlined"
        size="small"
        disabled={!isPar}
        onValueChange={(values) => { if (isPar) onEditValue(values.floatValue ? values.floatValue / 100 : 0); }}
        sx={{ width: PERCENT_WIDTH, ...inputSx }}
      />

      <NumericFormat
        value={debtServicePercent}
        customInput={TextField}
        thousandSeparator=","
        suffix="%"
        decimalScale={3}
        allowNegative={false}
        variant="outlined"
        size="small"
        disabled={!isDebtService}
        onValueChange={(values) => { if (isDebtService) onEditValue(values.floatValue ? values.floatValue / 100 : 0); }}
        sx={{ width: PERCENT_WIDTH, ...inputSx }}
      />

      <FormControl size="small" sx={{ width: SELECT_WIDTH }}>
        <Select
          value={selected}
          onChange={(e) => onEditType(e.target.value as CorrectionTarget)}
          sx={{ '& .MuiSelect-select': { fontSize: '14px' } }}
        >
          <MenuItem value="amount">Dollar</MenuItem>
          <MenuItem value="par amount">Par %</MenuItem>
          <MenuItem value="debt service">DS %</MenuItem>
        </Select>
      </FormControl>

      <DeleteOutlineIcon
        onClick={onRemove}
        sx={{ cursor: "pointer", "&:hover": { opacity: 0.75 } }}
        fontSize="small"
        color="error"
      />
    </Stack>
  );
}
